/**
 * Add-ons on a SALE (2026-09-23, Petal_Some Ug + Moving Ads; approved by
 * Frank: "yes I agree we add it on").
 *
 * A product's "Comes with / add-ons" list is the same list a hire business
 * uses as its hire kit (`products.hireKit[]`) — one list, two uses:
 *   • on a HIRE the pieces go out as the kit and are ticked back in at
 *     return; a piece with a `dailyRate` is charged per day (lib/config/hire.ts);
 *   • on a SALE they go with the item: a piece with a `price` is charged, a
 *     piece linked to a real product (`productId` — perfumes, chocolates the
 *     shop also sells alone) moves THAT product's stock, and a free unlinked
 *     piece is just what the item comes with ("with 2 × Batteries").
 *
 * On a sale a piece's count is PER ITEM — "each bouquet comes with 2 bars" —
 * so two bouquets take four bars. The till lets the desk remove a piece the
 * client is not taking, change its count, or add one.
 *
 * A SERVICE has the same list (Petal_Some Ug., Oct 2026: her bouquets are
 * made-to-order services, and a bouquet comes with chocolates or a teddy
 * bear). It is never hired, so its pieces carry no price per day; at the till
 * its add-ons become lines exactly as a product's do.
 */

export interface AddOnPiece {
  name: string;
  /** How many go with EACH item on a sale (with the whole line on a hire). */
  qtyOut: number;
  /** UGX each on a sale; absent/0 = free. */
  price?: number;
  /** A real product: its stock moves when this goes out with a sale. */
  productId?: string;
  /** Hire only: UGX per day per piece. */
  dailyRate?: number;
}

const num = (x: unknown): number => {
  const n = Number(x);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/** The pieces that actually go (a name and at least one of them). */
function going(pieces: ReadonlyArray<Partial<AddOnPiece>> | null | undefined): AddOnPiece[] {
  return (pieces || [])
    .filter((p): p is AddOnPiece => !!p && !!String(p.name || "").trim() && Math.round(Number(p.qtyOut) || 0) > 0)
    .map((p) => ({ ...p, name: String(p.name).trim(), qtyOut: Math.round(Number(p.qtyOut)) }));
}

/** A piece that becomes its own line on the sale: charged, or a real product whose stock moves. */
export function isLineAddOn(p: Partial<AddOnPiece>): boolean {
  return num(p?.price) > 0 || !!p?.productId;
}

/** What a line's add-ons add to the sale, for `lineQty` items. */
export function addOnsExtraFor(pieces: ReadonlyArray<Partial<AddOnPiece>> | null | undefined, lineQty: number): number {
  const q = Math.max(1, Math.round(lineQty || 1));
  return going(pieces).reduce((sum, p) => sum + Math.round(num(p.price) * p.qtyOut * q), 0);
}

/** "with 2 × Batteries, 1 × Charger" — the free, unlinked pieces, printed under the item. */
export function addOnNoteFor(pieces: ReadonlyArray<Partial<AddOnPiece>> | null | undefined, lineQty: number): string {
  const q = Math.max(1, Math.round(lineQty || 1));
  const parts = going(pieces)
    .filter((p) => !isLineAddOn(p))
    .map((p) => `${p.qtyOut * q} × ${p.name}`);
  return parts.length ? `with ${parts.join(", ")}` : "";
}

export interface AddOnLine {
  /** "product" moves the linked product's stock; "typed" is a charged extra with no stock behind it. */
  kind: "product" | "typed";
  productId?: string;
  name: string;
  quantity: number;
  /** UGX each (0 for a free linked product). */
  price: number;
}

/** The lines a sale gains from one item's add-ons, in the order they are listed. */
export function addOnLinesFor(pieces: ReadonlyArray<Partial<AddOnPiece>> | null | undefined, lineQty: number): AddOnLine[] {
  const q = Math.max(1, Math.round(lineQty || 1));
  return going(pieces)
    .filter(isLineAddOn)
    .map((p) => ({
      kind: p.productId ? ("product" as const) : ("typed" as const),
      productId: p.productId || undefined,
      name: `+ ${p.name}`,
      quantity: p.qtyOut * q,
      price: Math.round(num(p.price)),
    }));
}

/** A piece as `products.hireKit` stores it: `qty` goes with EACH item. */
export interface StoredPiece {
  name?: string | null;
  qty?: number | null;
  price?: number | null;
  productId?: string | null;
  dailyRate?: number | null;
}

/** The stored list in the till's shape (`qtyOut`), unnamed pieces dropped. */
export function tillPiecesFrom(kit: ReadonlyArray<StoredPiece | null | undefined> | null | undefined): AddOnPiece[] {
  return (Array.isArray(kit) ? kit : [])
    .filter((piece): piece is StoredPiece => !!piece && !!String(piece.name || "").trim())
    .map((piece) => ({
      name: String(piece.name),
      qtyOut: Math.max(1, Number(piece.qty) || 1),
      ...(num(piece.dailyRate) > 0 ? { dailyRate: Number(piece.dailyRate) } : {}),
      ...(num(piece.price) > 0 ? { price: Number(piece.price) } : {}),
      ...(piece.productId ? { productId: String(piece.productId) } : {}),
    }));
}

/** A service is never hired: its pieces keep no price per day. */
export function withoutHireRates<T extends { dailyRate?: number }>(pieces: T[] | undefined): Array<Omit<T, "dailyRate">> | undefined {
  return pieces?.map(({ dailyRate: _rate, ...rest }) => rest);
}

/** "Teddy bear", "2 × Chocolates" — every piece going with `lineQty` items, by name. */
export function addOnNamesFor(pieces: ReadonlyArray<Partial<AddOnPiece>> | null | undefined, lineQty: number): string[] {
  const q = Math.max(1, Math.round(lineQty || 1));
  return going(pieces).map((p) => (p.qtyOut * q > 1 ? `${p.qtyOut * q} × ${p.name}` : p.name));
}

// ─── At the till ──────────────────────────────────────────────────────────

/** A till line, as far as its add-ons are concerned. */
export interface AddOnTillLine {
  itemType?: string;
  productId?: string;
  serviceId?: string;
  /** A booking finished at the till (Continue at the till). */
  bookingId?: string;
}

/**
 * The product whose add-ons a till line carries: a product line's own, or a
 * service's — a service picked from the till's Services list carries its
 * product id as `serviceId` (services-as-products). None for a typed (b2b)
 * line. A booking finished at the till brings its add-ons like any service
 * (Petal, Oct 2026: the bouquet's chocolates join the one sale) — the till
 * sale is not the booking's own sale, which stays services only and is never
 * built here.
 */
export function addOnSourceId(line: AddOnTillLine | null | undefined): string | undefined {
  if (!line) return undefined;
  if (line.itemType === "product") return line.productId || undefined;
  if (line.itemType === "service") return line.serviceId || undefined;
  return undefined;
}

/** A line's options (the note "comes with …" rides here). */
export type LineOption = { group: string; choice: string; price?: number };

/** A line an add-on adds to the sale, in completeSale's item shape. */
export type AddOnSaleLine =
  | { itemType: "product"; productId: string; imageUrl?: string; name: string; price: number; quantity: number }
  | { itemType: "b2b"; name: string; price: number; quantity: number; costPrice: number; profitAmount: number };

/**
 * A sale line followed by its add-ons, the way the till sends them to
 * completeSale — the same for a product and a service: the free unlinked
 * pieces as the note "with N × Name" on the line, then each piece that is a
 * line of its own in the order listed — a linked product as a product line
 * whose stock moves, a charged unlinked piece as a typed "+ Name" line (cost
 * 0, all margin).
 */
export function withAddOnLines<L extends { quantity: number; options?: LineOption[] }>(
  line: L,
  pieces: ReadonlyArray<Partial<AddOnPiece>> | null | undefined,
  imageOf?: (productId: string) => string | undefined,
): Array<L | AddOnSaleLine> {
  const note = addOnNoteFor(pieces, line.quantity);
  const parent: L = note ? { ...line, options: [...(line.options || []), { group: "comes with", choice: note }] } : line;
  const extras: AddOnSaleLine[] = addOnLinesFor(pieces, line.quantity).map((l) =>
    l.kind === "product" && l.productId
      ? {
          itemType: "product" as const,
          productId: l.productId,
          imageUrl: imageOf?.(l.productId),
          name: l.name,
          price: l.price,
          quantity: l.quantity,
        }
      : {
          itemType: "b2b" as const,
          name: l.name,
          price: l.price,
          quantity: l.quantity,
          costPrice: 0,
          profitAmount: l.price * l.quantity,
        },
  );
  return [parent, ...extras];
}
